/**
 * Design briefs — stable `SkyboxTheme` IDs; public names match `registry.ts`.
 * Shader-only (non-photoreal), roughly matching what the globe actually reads as.
 */

import type { SkyboxTheme } from './skyboxes/types';

export interface SkyboxDesignBrief {
  id: SkyboxTheme;
  publicName: string;
  themeDirection: string;
  equirectMood: string;
  heroColor: string;
  ultraOnlyMotion: string;
  brightSkyHudRisk: 'low' | 'high';
  graphicsNotes: string;
}

export const SKYBOX_BRIEFS: Record<SkyboxTheme, SkyboxDesignBrief> = {
  sky: {
    id: 'sky',
    publicName: 'Open Sky',
    themeDirection:
      '**Blue dome** in **all directions** — board reads as **floating in cloud**; **volumetric** mist below, **puffy** cumulus, sun glow, birds.',
    equirectMood: 'Continuous sky-blue; no grey “ground” patch.',
    heroColor: '#3b9ae8',
    ultraOnlyMotion: 'Slow cloud drift + sun-stable shimmer; birds glide.',
    brightSkyHudRisk: 'high',
    graphicsNotes: 'Bright; cool fill; `data-skybox-bright` with Homestead & Desert.',
  },
  aurora: {
    id: 'aurora',
    publicName: 'Aurora',
    themeDirection:
      '**Night**: **bright electric green** and **violet** **aurora curtains** that **flow** and drift (3D noise), with **twin moons**, **stars**, and a dim ridge read.',
    equirectMood: 'Luminous green / purple bands, magical night sky.',
    heroColor: '#40ff8a',
    ultraOnlyMotion: 'Ribbon phase drift, moon/slow cloud.',
    brightSkyHudRisk: 'low',
    graphicsNotes: 'Darker hub; keep HUD treatment opaque vs Open Sky / Homestead.',
  },
  medieval: {
    id: 'medieval',
    publicName: 'Dusk',
    themeDirection:
      '**Outdoor dusk**: indigo **zenith** → amber **horizon**; **mountain** ridge (fbm on direction, seam-free), **low clouds**, warm **ember** motes.',
    equirectMood: 'Back-country range, ember haze.',
    heroColor: '#6a2a1a',
    ultraOnlyMotion: 'Cloud drift + per-mote ember twinkle.',
    brightSkyHudRisk: 'low',
    graphicsNotes: 'Atmospheric; horizon-led composition.',
  },
  'sci-fi': {
    id: 'sci-fi',
    publicName: 'Nebula',
    themeDirection:
      '**Nebula**-forward void: indigo / **magenta** / **cyan** / **gold** dust, **rift core** at zenith, subtle **lattice** hint, dense **twinkle** stars — no hero planet discs.',
    equirectMood: 'Dark void + saturated gas, high contrast accents.',
    heroColor: '#b060d0',
    ultraOnlyMotion: 'Gas drift; lattice phase; star twinkle.',
    brightSkyHudRisk: 'low',
    graphicsNotes: 'Cool key + purple rim; env map leans nebula core.',
  },
  farm: {
    id: 'farm',
    publicName: 'Homestead',
    themeDirection:
      'Hillcrests frame a **V** into the **valley**; **wood** buildings, **sunset** wash and long warm haze, birds, rolling hills.',
    equirectMood: 'Golden hour, pastoral, calm.',
    heroColor: '#ff8a4a',
    ultraOnlyMotion: 'Cloud drift, birds, subtle hill sway.',
    brightSkyHudRisk: 'high',
    graphicsNotes: 'Bright warm sunset; `data-skybox-bright` with Open Sky.',
  },
  desert: {
    id: 'desert',
    publicName: 'Desert',
    themeDirection:
      '**Arid**: **bright azure** sky, harsh **sun**; **sand dune** silhouettes below horizon (fbm ridges), warm **tan** ground colour.',
    equirectMood: 'Clear blue over warm dunes; high key.',
    heroColor: '#7ec0ff',
    ultraOnlyMotion: 'Soft dune drift in time; sun stable.',
    brightSkyHudRisk: 'high',
    graphicsNotes: 'Bright; sandstone plinth default; warm-tinted scene fog.',
  },
  sanctum: {
    id: 'sanctum',
    publicName: 'Sanctum',
    themeDirection:
      '**Inner sanctum** of a secret cabal: black-void chamber with a **crimson sigil** rotating slowly overhead, **indigo incense** curtains drifting through the upper hemisphere, dim **runic glyph** stripes around the equator, a **horizon ember** band smouldering at the floor, and sparse warm **candle motes**.',
    equirectMood: 'Deep void with a crimson focal point at the zenith.',
    heroColor: '#d83048',
    ultraOnlyMotion: 'Sigil rotation, ember flicker, smoke drift, mote twinkle.',
    brightSkyHudRisk: 'low',
    graphicsNotes:
      'Darkest hub; reserved for the Boardroom (premium tier — hidden from the launch picker).',
  },
};
